import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {
  AddOnRateType,
  patchV1CompaniesCompanyIdAutoAddOnAssignmentsId,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { AddOnAssignment } from '~hooks/useAddOns/models';
import connection from '~services/connectionModule';

import AddOnHelpLinks from './AddOnHelpLinks';
import { AddOnDTO, addOnSchema, getAddOnRateTypeOptionLabel } from './AutomaticAddOns';
import InstallmentsFieldHelper from './InstallmentsFieldHelper';
import InstallmentsHelperText from './InstallmentsHelperText';

interface AutomaticAddOnEditDialogProps {
  companyId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedAutoAddOnAssignment: AddOnAssignment | null;
}

const addOnRateTypeOptionsList = Object.values(AddOnRateType);

const AutomaticAddOnEditDialog = ({
  companyId,
  open,
  onClose,
  onSuccess,
  selectedAutoAddOnAssignment,
}: AutomaticAddOnEditDialogProps) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const updateAutomaticAddOn = async (data: AddOnDTO) => {
    if (!selectedAutoAddOnAssignment) {
      return;
    }

    setIsUpdating(true);
    try {
      await patchV1CompaniesCompanyIdAutoAddOnAssignmentsId({
        path: { 'company-id': companyId, id: selectedAutoAddOnAssignment.id },
        body: {
          name: data.name,
          quantity: data.quantity,
          revenue_rate_value:
            data.rateType === AddOnRateType.RATE_FOR_EACH ||
            data.rateType === AddOnRateType.PER_LOAD
              ? data.rateValue?.toString()
              : undefined,
          percentage:
            data.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
              ? data.rateValue
              : undefined,
          revenue_cost_rate_type: data.rateType as AddOnRateType,
          cost_rate_value: data.costRateValue ? String(data.costRateValue) : undefined,
          total_installments: data.totalInstallments,
        },
      });
      onSuccess();
    } catch (error) {
      console.error(error);
      connection.handleRequestError(
        error,
        t('error_messages.auto_add_on_assignments.failed_to_update'),
        [422],
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const form = useForm<AddOnDTO>({
    resolver: yupResolver(addOnSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: selectedAutoAddOnAssignment || undefined,
  });

  const watchedAddOn = form.watch();

  useEffect(() => {
    if (selectedAutoAddOnAssignment) {
      const rateValue =
        selectedAutoAddOnAssignment.revenueCostRateType ===
        AddOnRateType.RATE_PERCENT_OF_TOTAL
          ? selectedAutoAddOnAssignment.percentage
          : (selectedAutoAddOnAssignment.rate ?? undefined);

      form.reset({
        name: selectedAutoAddOnAssignment.name,
        quantity: selectedAutoAddOnAssignment.quantity,
        rateValue: rateValue,
        rateType: selectedAutoAddOnAssignment.revenueCostRateType ?? '',
        accountingId: selectedAutoAddOnAssignment.accountingId,
        totalInstallments: selectedAutoAddOnAssignment.totalInstallments,
        costRateValue: selectedAutoAddOnAssignment.costRateValue ?? undefined,
      });
    }
  }, [selectedAutoAddOnAssignment, form.reset]);

  const onSubmit = useCallback(() => {
    form.handleSubmit(async (data) => {
      await updateAutomaticAddOn(data);
    })();
  }, [form.handleSubmit, updateAutomaticAddOn]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!selectedAutoAddOnAssignment) {
    return null;
  }

  return (
    <Dialog isOpen={open} onClose={onClose} maxWidth="md">
      <DialogHeader onClose={handleClose}>
        {t('administration.automatic_settlement_add_ons.edit_add_on')}
      </DialogHeader>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 4 }}>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              label={t('administration.automatic_settlement_add_ons.add_on')}
              name="name"
              type="text"
            />
          </Grid>

          <Grid size={{ xs: 2 }}>
            <AutocompleteFormField
              control={form.control}
              disablePortal={false}
              errors={form.formState.errors}
              getLabel={(item: AddOnRateType) => getAddOnRateTypeOptionLabel(item)}
              getValue={(item: AddOnRateType) => item}
              isRequired
              label={t('administration.rates.automatic_add_ons.rate_type')}
              list={addOnRateTypeOptionsList}
              name="rateType"
            />
          </Grid>

          <Grid size={{ xs: 1.5 }}>
            <TextFormField
              control={form.control}
              // A percentage is taken from the settlement's own total, so the
              // backend never multiplies it by this quantity.
              disabled={watchedAddOn.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL}
              errors={form.formState.errors}
              label={t('administration.rates.automatic_add_ons.quantity')}
              name="quantity"
              type="number"
            />
          </Grid>

          <Grid size={{ xs: 1.5 }}>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              label={t('administration.rates.add_ons.cost_rate')}
              name="costRateValue"
              type="number"
            />
          </Grid>

          <Grid size={{ xs: 1.5 }}>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              label={t('administration.rates.invoice_rate')}
              name="rateValue"
              type="number"
            />
          </Grid>

          <Grid size={{ xs: 1.5 }}>
            <TextFormField
              control={form.control}
              errors={form.formState.errors}
              label={t('administration.rates.automatic_add_ons.total_installments')}
              name="totalInstallments"
              type="number"
            />
            <InstallmentsFieldHelper totalInstallments={watchedAddOn.totalInstallments} />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <InstallmentsHelperText
              quantity={watchedAddOn.quantity}
              rateType={watchedAddOn.rateType}
              rateValue={watchedAddOn.rateValue}
              totalInstallments={watchedAddOn.totalInstallments}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Box sx={{ mr: 'auto' }}>
          <AddOnHelpLinks />
        </Box>

        <SmallButton variant="outlined" color="secondary" onClick={handleClose}>
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton color="primary" loading={isUpdating} onClick={onSubmit}>
          {t('actions.save')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AutomaticAddOnEditDialog;
